import React, { useState, useCallback, KeyboardEvent } from 'react';
import { Button, TextField, Box } from '@mui/material';
import { LocalOffer as TagIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface InlineLabelEditorProps {
  initialLabel?: string;
  onLabelSave: (label: string | null) => Promise<void>;
  placeholder?: string;
  disabled?: boolean;
}

const InlineLabelEditor: React.FC<InlineLabelEditorProps> = ({
  initialLabel,
  onLabelSave,
  placeholder,
  disabled = false
}) => {
  const { t } = useTranslation();
  const defaultPlaceholder = placeholder || t('identities.addLabel');
  const [isEditing, setIsEditing] = useState(false);
  const [labelValue, setLabelValue] = useState(initialLabel || '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [announceMessage, setAnnounceMessage] = useState<string>('');

  // Function to announce messages to screen readers
  const announceToScreenReader = useCallback((message: string) => {
    setAnnounceMessage(message);
    // Clear the message after a short delay to allow for re-announcements
    setTimeout(() => setAnnounceMessage(''), 100);
  }, []);

  // Handle entering edit mode
  const enterEditMode = useCallback(() => {
    setIsEditing(true);
    announceToScreenReader(t('identities.editingLabel'));
  }, [announceToScreenReader, t]);

  // Handle exiting edit mode
  const exitEditMode = useCallback((saved: boolean = false) => {
    setIsEditing(false);
    if (saved) {
      announceToScreenReader(t('identities.labelSaved'));
    } else {
      announceToScreenReader(t('identities.editingCancelled'));
    }
  }, [announceToScreenReader, t]);

  const handleSave = useCallback(async () => {
    if (isSaving) return;
    
    setIsSaving(true);
    setError(null);
    
    try {
      const trimmedLabel = labelValue.trim();
      await onLabelSave(trimmedLabel || null);
      exitEditMode(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save label');
    } finally {
      setIsSaving(false);
    }
  }, [labelValue, onLabelSave, isSaving, exitEditMode]);

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      setLabelValue(initialLabel || '');
      setError(null);
      exitEditMode(false);
    }
  }, [handleSave, initialLabel, exitEditMode]);

  // Click-to-edit with proper accessibility
  if (!isEditing) {
    return (
      <Box>
        {/* Screen reader announcements */}
        <div 
          aria-live="polite" 
          aria-atomic="true" 
          style={{ 
            position: 'absolute', 
            left: '-10000px', 
            width: '1px', 
            height: '1px', 
            overflow: 'hidden' 
          }}
        >
          {announceMessage}
        </div>
        <Button
        size="small"
        onClick={enterEditMode}
        disabled={disabled}
        variant={initialLabel ? "outlined" : "text"}
        startIcon={<TagIcon sx={{ fontSize: '0.75rem !important' }} />}
        sx={{ 
          textTransform: 'none',
          color: initialLabel ? 'primary.main' : 'text.secondary',
          justifyContent: 'flex-start',
          borderColor: initialLabel ? 'primary.main' : 'transparent',
          backgroundColor: initialLabel ? 'primary.50' : 'transparent',
          minHeight: '24px',
          borderRadius: '12px',
          fontSize: '0.75rem',
          px: initialLabel ? 1.5 : 1,
          '&:hover': {
            backgroundColor: initialLabel ? 'primary.100' : 'action.hover',
            borderColor: initialLabel ? 'primary.dark' : 'text.secondary'
          },
          '& .MuiButton-startIcon': {
            marginRight: '4px',
            marginLeft: 0
          }
        }}
        aria-label={initialLabel ? `Edit label: ${initialLabel}` : 'Add label'}
      >
        {initialLabel || defaultPlaceholder}
      </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Screen reader announcements */}
      <div 
        aria-live="polite" 
        aria-atomic="true" 
        style={{ 
          position: 'absolute', 
          left: '-10000px', 
          width: '1px', 
          height: '1px', 
          overflow: 'hidden' 
        }}
      >
        {announceMessage}
      </div>
      <TextField
      size="small"
      value={labelValue}
      onChange={(e) => setLabelValue(e.target.value)}
      onKeyDown={handleKeyDown}
      onBlur={handleSave}
      disabled={isSaving}
      error={!!error}
      helperText={error}
      autoFocus
      placeholder={defaultPlaceholder}
      slotProps={{ 
        htmlInput: { 
          maxLength: 100,
          'aria-label': 'Edit label'
        }
      }}
      sx={{ 
        minWidth: 120,
        '& .MuiInputBase-root': {
          fontSize: '0.875rem',
          height: '32px'
        }
      }}
    />
    </Box>
  );
};

export default InlineLabelEditor;